// src/components/pages/MonthlyBook.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  FaArrowLeft, FaCalendarAlt, FaCheck, FaLeaf, FaFish, 
  FaPlus, FaMinus, FaSpinner, FaInfoCircle, FaClock,
  FaTrash, FaCalendarDay
} from 'react-icons/fa';
import { getColors } from '../../styles/colors';
import { createSubscriptionAsync } from '../../redux/slices/subscriptionSlice';

const MonthlyBook = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useSelector((state) => state.theme.mode);
  const isDark = theme === 'dark';
  const colors = getColors(isDark);
  const { isLoading } = useSelector((state) => state.subscription || {});
  
  // State
  const [startDate, setStartDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [localError, setLocalError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // ✅ Meal state with separate Veg and Non-Veg quantities for each meal
  const [mealItems, setMealItems] = useState([
    { 
      meal_time: 'Breakfast', 
      time_slot: '7:30 AM - 10:00 AM',
      veg: { selected: true, quantity: 1 }, 
      nonVeg: { selected: false, quantity: 0 } 
    },
    { 
      meal_time: 'Lunch', 
      time_slot: '12:00 PM - 2:30 PM',
      veg: { selected: true, quantity: 1 }, 
      nonVeg: { selected: false, quantity: 0 } 
    },
    { 
      meal_time: 'Dinner', 
      time_slot: '7:30 PM - 10:00 PM',
      veg: { selected: true, quantity: 1 }, 
      nonVeg: { selected: false, quantity: 0 } 
    }
  ]);

  const mealPrices = { Breakfast: 50, Lunch: 70, Dinner: 90 };

  // ✅ Update veg quantity
  const updateVegQuantity = (index, delta) => {
    setMealItems(prev => {
      const newItems = [...prev];
      const newQty = Math.max(0, newItems[index].veg.quantity + delta);
      newItems[index].veg.quantity = newQty;
      newItems[index].veg.selected = newQty > 0;
      return newItems;
    });
  };

  // ✅ Update non-veg quantity
  const updateNonVegQuantity = (index, delta) => {
    setMealItems(prev => {
      const newItems = [...prev];
      const newQty = Math.max(0, newItems[index].nonVeg.quantity + delta);
      newItems[index].nonVeg.quantity = newQty;
      newItems[index].nonVeg.selected = newQty > 0;
      return newItems;
    });
  };

  // ✅ Toggle veg selection
  const toggleVeg = (index) => {
    setMealItems(prev => {
      const newItems = [...prev];
      const currentlySelected = newItems[index].veg.selected;
      newItems[index].veg.selected = !currentlySelected;
      if (!newItems[index].veg.selected) {
        newItems[index].veg.quantity = 0;
      } else if (newItems[index].veg.quantity === 0) {
        newItems[index].veg.quantity = 1;
      }
      return newItems;
    });
  };

  // ✅ Toggle non-veg selection
  const toggleNonVeg = (index) => {
    setMealItems(prev => {
      const newItems = [...prev];
      const currentlySelected = newItems[index].nonVeg.selected;
      newItems[index].nonVeg.selected = !currentlySelected;
      if (!newItems[index].nonVeg.selected) {
        newItems[index].nonVeg.quantity = 0;
      } else if (newItems[index].nonVeg.quantity === 0) {
        newItems[index].nonVeg.quantity = 1;
      }
      return newItems;
    });
  };

  // ✅ Get total items count
  const getTotalItems = () => {
    let count = 0;
    mealItems.forEach(meal => {
      if (meal.veg.selected && meal.veg.quantity > 0) count += meal.veg.quantity;
      if (meal.nonVeg.selected && meal.nonVeg.quantity > 0) count += meal.nonVeg.quantity;
    });
    return count;
  };

  // ✅ Get total price
  const getTotalPrice = () => {
    let total = 0;
    mealItems.forEach(meal => {
      const price = mealPrices[meal.meal_time] || 60;
      if (meal.veg.selected && meal.veg.quantity > 0) {
        total += price * meal.veg.quantity;
      }
      if (meal.nonVeg.selected && meal.nonVeg.quantity > 0) {
        total += price * meal.nonVeg.quantity;
      }
    });
    return total;
  };

  // ✅ Format date
  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // ✅ Get meal icon
  const getMealIcon = (mealTime) => {
    const icons = {
      'Breakfast': '🌅',
      'Lunch': '☀️',
      'Dinner': '🌙'
    };
    return icons[mealTime] || '🍽️';
  };

  // ✅ Build subscription items
  const buildSubscriptionItems = () => {
    const items = [];
    mealItems.forEach(meal => {
      if (meal.veg.selected && meal.veg.quantity > 0) {
        items.push({
          meal_time: meal.meal_time,
          meal_type: 'Veg',
          quantity: meal.veg.quantity
        });
      }
      if (meal.nonVeg.selected && meal.nonVeg.quantity > 0) {
        items.push({
          meal_time: meal.meal_time,
          meal_type: 'Non-Veg',
          quantity: meal.nonVeg.quantity
        });
      }
    });
    return items;
  };

  // ✅ Handle subscription creation
  const handleCreateSubscription = async () => {
    setLocalError(null);
    setSuccessMessage(null);

    const items = buildSubscriptionItems();

    if (items.length === 0) {
      setLocalError('Please select at least one meal');
      return;
    }

    const payload = {
      start_date: startDate.toISOString().split('T')[0],
      items: items
    };

    if (deliveryAddress.trim()) {
      payload.delivery_address = deliveryAddress.trim();
    }

    console.log('📤 Creating subscription payload:', payload);

    try {
      const result = await dispatch(createSubscriptionAsync(payload)).unwrap();
      console.log('✅ Subscription created:', result);

      if (result.status) {
        const data = result.data;
        setSuccessMessage(`🎉 Subscription created successfully!`);
        
        // Save to localStorage for My Orders
        const savedSubscriptions = JSON.parse(localStorage.getItem('subscriptions') || '[]');
        const newSubscription = {
          ...data,
          id: data.subscription_id,
          items: items,
          created_at: new Date().toISOString()
        };
        savedSubscriptions.unshift(newSubscription);
        localStorage.setItem('subscriptions', JSON.stringify(savedSubscriptions));
        
        setTimeout(() => {
          navigate('/my-orders');
        }, 2000);
      }
    } catch (err) {
      console.error('❌ Subscription failed:', err);
      let errorMsg = typeof err === 'string' ? err : 'Failed to create subscription';
      if (err?.message) {
        errorMsg = err.message;
      }
      setLocalError(errorMsg);
    }
  };

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: colors.background }}>
      <div className="max-w-4xl mx-auto">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 mb-6 transition-all hover:scale-105" 
          style={{ color: colors.primary }}
        >
          <FaArrowLeft /> Back
        </button>

        <div className="rounded-2xl p-8" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
          <div className="text-center mb-8">
            <div className="inline-block bg-gradient-to-r from-purple-500 to-purple-600 px-4 py-1 rounded-full text-xs font-bold text-white mb-4 shadow-lg">
              📅 MONTHLY SUBSCRIPTION
            </div>
            <h1 className="text-3xl font-bold" style={{ color: colors.text }}>Monthly Tiffin Subscription</h1>
            <p className="text-sm mt-2" style={{ color: colors.textSecondary }}>
              Select Veg & Non-Veg meals with different quantities
            </p>
          </div>

          {/* Success/Error Messages */}
          {successMessage && (
            <div className="p-3 rounded-xl mb-4 text-sm flex items-center gap-2" style={{ 
              backgroundColor: '#22C55E20',
              color: '#22C55E',
              border: '1px solid #22C55E'
            }}>
              <FaCheck /> {successMessage}
            </div>
          )}
          {localError && (
            <div className="p-3 rounded-xl mb-4 text-sm" style={{ 
              backgroundColor: '#FEF2F2',
              color: '#DC2626',
              border: '1px solid #FECACA'
            }}>
              ❌ {localError}
            </div>
          )}

          {/* Start Date */}
          <div className="mb-6">
            <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
              <FaCalendarDay className="inline mr-2" /> Start Date
            </label>
            <div 
              className="flex items-center justify-between p-3 rounded-xl cursor-pointer hover:shadow-md transition-all" 
              style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.border}` }} 
              onClick={() => setShowCalendar(!showCalendar)}
            >
              <span className="text-sm font-medium" style={{ color: colors.text }}>{formatDate(startDate)}</span>
              <FaCalendarAlt style={{ color: colors.primary }} />
            </div>
            {showCalendar && (
              <div className="mt-2 p-3 rounded-xl" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
                <input 
                  type="date" 
                  value={startDate.toISOString().split('T')[0]} 
                  onChange={(e) => {
                    if (e.target.value) {
                      setStartDate(new Date(e.target.value));
                      setShowCalendar(false);
                    }
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full p-3 rounded-xl focus:outline-none"
                  style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.border}`, color: colors.text }}
                />
              </div>
            )}
          </div>

          {/* Delivery Address */}
          <div className="mb-6">
            <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
              Delivery Address <span className="text-xs" style={{ color: colors.textSecondary }}>(Optional)</span>
            </label>
            <input
              type="text"
              value={deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
              placeholder="Enter your delivery address"
              className="w-full p-3 rounded-xl focus:outline-none"
              style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.border}`, color: colors.text }}
            />
          </div>

          {/* ✅ Meal Selection - Veg & Non-Veg side by side */}
          <div className="mb-6">
            <label className="text-sm font-medium block mb-3" style={{ color: colors.textSecondary }}>
              Select Meals <span className="text-xs">(Veg & Non-Veg with separate quantities)</span>
            </label>
            <div className="space-y-4">
              {mealItems.map((meal, index) => (
                <div 
                  key={index} 
                  className="rounded-xl p-4" 
                  style={{ 
                    backgroundColor: colors.inputBackground, 
                    border: `1px solid ${colors.border}`
                  }}
                >
                  {/* Meal Header */}
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xl">{getMealIcon(meal.meal_time)}</span>
                    <div>
                      <h4 className="font-bold" style={{ color: colors.text }}>{meal.meal_time}</h4>
                      <p className="text-xs" style={{ color: colors.textSecondary }}>
                        <FaClock className="inline mr-1 text-[10px]" /> {meal.time_slot}
                      </p>
                    </div>
                    <span className="ml-auto text-xs px-2 py-1 rounded-full" style={{ 
                      backgroundColor: colors.primary + '20',
                      color: colors.primary
                    }}>
                      ₹{mealPrices[meal.meal_time]} / meal
                    </span>
                  </div>

                  {/* ✅ Veg & Non-Veg side by side in one row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Veg */}
                    <div 
                      className={`flex items-center justify-between p-3 rounded-lg transition-all cursor-pointer ${
                        meal.veg.selected ? 'border-2 border-green-500' : 'border border-gray-300'
                      }`}
                      style={{ 
                        backgroundColor: meal.veg.selected ? '#22C55E10' : colors.background,
                      }}
                      onClick={() => toggleVeg(index)}
                    >
                      <div className="flex items-center gap-2">
                        <FaLeaf className="text-green-500" />
                        <span className="text-sm font-medium" style={{ color: colors.text }}>Veg</span>
                      </div>
                      {meal.veg.selected ? (
                        <div className="flex items-center gap-2">
                          <button 
                            className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-105"
                            style={{ backgroundColor: '#EF4444', color: 'white' }}
                            onClick={(e) => { e.stopPropagation(); updateVegQuantity(index, -1); }}
                          >
                            <FaMinus className="text-xs" />
                          </button>
                          <span className="font-bold min-w-[20px] text-center" style={{ color: colors.text }}>
                            {meal.veg.quantity}
                          </span>
                          <button 
                            className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-105"
                            style={{ backgroundColor: '#22C55E', color: 'white' }}
                            onClick={(e) => { e.stopPropagation(); updateVegQuantity(index, 1); }}
                          >
                            <FaPlus className="text-xs" />
                          </button>
                        </div>
                      ) : (
                        <button 
                          className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-105"
                          style={{ backgroundColor: colors.border, color: colors.textSecondary }}
                          onClick={(e) => { e.stopPropagation(); toggleVeg(index); }}
                        >
                          <FaPlus className="text-xs" />
                        </button>
                      )}
                    </div>

                    {/* Non-Veg */}
                    <div 
                      className={`flex items-center justify-between p-3 rounded-lg transition-all cursor-pointer ${
                        meal.nonVeg.selected ? 'border-2 border-red-500' : 'border border-gray-300'
                      }`}
                      style={{ 
                        backgroundColor: meal.nonVeg.selected ? '#EF444410' : colors.background,
                      }}
                      onClick={() => toggleNonVeg(index)}
                    >
                      <div className="flex items-center gap-2">
                        <FaFish className="text-red-500" />
                        <span className="text-sm font-medium" style={{ color: colors.text }}>Non-Veg</span>
                      </div>
                      {meal.nonVeg.selected ? (
                        <div className="flex items-center gap-2">
                          <button 
                            className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-105"
                            style={{ backgroundColor: '#EF4444', color: 'white' }}
                            onClick={(e) => { e.stopPropagation(); updateNonVegQuantity(index, -1); }}
                          >
                            <FaMinus className="text-xs" />
                          </button>
                          <span className="font-bold min-w-[20px] text-center" style={{ color: colors.text }}>
                            {meal.nonVeg.quantity}
                          </span>
                          <button 
                            className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-105"
                            style={{ backgroundColor: '#22C55E', color: 'white' }}
                            onClick={(e) => { e.stopPropagation(); updateNonVegQuantity(index, 1); }}
                          >
                            <FaPlus className="text-xs" />
                          </button>
                        </div>
                      ) : (
                        <button 
                          className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-105"
                          style={{ backgroundColor: colors.border, color: colors.textSecondary }}
                          onClick={(e) => { e.stopPropagation(); toggleNonVeg(index); }}
                        >
                          <FaPlus className="text-xs" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div className="p-4 rounded-xl mb-6" style={{ backgroundColor: colors.primary + '10', border: `1px solid ${colors.primary}30` }}>
            <h4 className="font-semibold flex items-center gap-2" style={{ color: colors.text }}>
              <FaInfoCircle className="text-xs" /> Order Summary
            </h4>
            <div className="flex justify-between text-sm mt-2">
              <span style={{ color: colors.textSecondary }}>
                {getTotalItems()} meal(s) • {formatDate(startDate)} onwards
              </span>
              <span className="font-bold text-lg" style={{ color: colors.primary }}>
                ₹{getTotalPrice()}
              </span>
            </div>
            <div className="flex justify-between text-xs mt-1" style={{ color: colors.textSecondary }}>
              <span>Deposit (50%): ₹{Math.round(getTotalPrice() * 0.5)}</span>
              <span>Outstanding: ₹{Math.round(getTotalPrice() * 0.5)}</span>
            </div>
          </div>

          {/* Confirm Button */}
          <button 
            className="w-full py-3.5 rounded-xl font-semibold text-white transition-all hover:scale-[1.02] flex items-center justify-center gap-2 disabled:opacity-50" 
            style={{ 
              backgroundColor: colors.primary, 
              boxShadow: `0 4px 20px ${colors.primary}40`,
              opacity: getTotalItems() > 0 ? 1 : 0.5,
              cursor: getTotalItems() > 0 ? 'pointer' : 'not-allowed'
            }} 
            onClick={handleCreateSubscription}
            disabled={isLoading || getTotalItems() === 0}
          >
            {isLoading ? (
              <><FaSpinner className="animate-spin" /> Creating Subscription...</>
            ) : (
              <><FaCalendarAlt className="inline mr-2" /> Confirm Monthly Subscription</>
            )}
          </button>

          {getTotalItems() === 0 && (
            <p className="text-center text-xs mt-2" style={{ color: colors.textSecondary }}>
              Please select at least one meal (Veg or Non-Veg)
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonthlyBook;